import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';
import api from './api';
import toast from 'react-hot-toast';

// Firebase configuration
// You should move these to environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app = null;
let messaging = null;

// Initialize Firebase
const initializeFirebase = async () => {
  try {
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser');
      return false;
    }
    
    if (!app) {
      app = initializeApp(firebaseConfig);
      messaging = getMessaging(app);
    }
    return true;
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    return false;
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Get FCM token
export const getFCMToken = async () => {
  try {
    const initialized = await initializeFirebase();
    if (!initialized) {
      throw new Error('Firebase Messaging is not supported in this browser');
    }

    const permission = await requestNotificationPermission();
    if (!permission) {
      console.warn('Notification permission denied');
      throw new Error('Notification permission denied');
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.error('VAPID key not configured');
      throw new Error('VAPID key not configured');
    }

    const token = await getToken(messaging, { vapidKey });
    console.log('FCM Token:', token);
    return token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    // Re-throw the error with the specific message
    throw error;
  }
};

// Store FCM token on server
export const storeFCMToken = async (deviceToken) => {
  try {
    const deviceType = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios' : 'android';
    
    const response = await api.post('/api/mobile/fcm/store-token', {
      token: deviceToken,
      device_type: deviceType,
    });
    
    return response.data;
  } catch (error) {
    console.error('Error storing FCM token:', error);
    
    // Check if token is invalid (401, 404, 410 indicate invalid token)
    if (error.response?.status === 422 || 
        error.response?.status === 404 || 
        error.response?.status === 410) {
      console.warn('Token rejected by server, marking as invalid');
      throw new Error('INVALID_TOKEN');
    }
    
    throw error;
  }
};

// Remove FCM token from server
export const removeFCMToken = async (deviceToken) => {
  try {
    const response = await api.post('/api/mobile/fcm/remove-token', {
      token: deviceToken,
    });
    
    return response.data;
  } catch (error) {
    console.error('Error removing FCM token:', error);
    throw error;
  }
};

// Get FCM status
export const getFCMStatus = async () => {
  try {
    const response = await api.get('/api/mobile/fcm/status');
    return response.data;
  } catch (error) {
    console.error('Error getting FCM status:', error);
    throw error;
  }
};

// Check if notifications are supported
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Check if notifications are enabled
export const isNotificationEnabled = () => {
  return isNotificationSupported() && Notification.permission === 'granted';
};

// Get stored FCM token from localStorage
export const getStoredFCMToken = () => {
  return localStorage.getItem('fcm_token');
};

// Store FCM token in localStorage
export const setStoredFCMToken = (token) => {
  if (token) {
    localStorage.setItem('fcm_token', token);
  } else {
    localStorage.removeItem('fcm_token');
  }
};

// Enable notifications
export const enableNotifications = async () => {
  try {
    // Check if we already have a valid token
    const existingToken = getStoredFCMToken();
    if (existingToken && isNotificationEnabled()) {
      console.log('Using existing FCM token:', existingToken);
      // Verify token is still registered on server
      try {
        await storeFCMToken(existingToken);
        setupForegroundMessageHandler();
        return { success: true, token: existingToken };
      } catch (error) {
        // Check if token is invalid
        if (error.message === 'INVALID_TOKEN') {
          console.log('Token invalid, deleting and generating new one');
          // Delete old token from Firebase
          await refreshFCMToken();
        } else {
          // Other errors, still clear the token
          console.log('Error with existing token, generating new one');
          setStoredFCMToken(null);
        }
      }
    }
    
    // Get new FCM token
    const token = await getFCMToken();

    // Store token on server
    await storeFCMToken(token);
    
    // Store token locally
    setStoredFCMToken(token);
    
    // Setup foreground message handler
    setupForegroundMessageHandler();
    
    return { success: true, token };
  } catch (error) {
    console.error('Error enabling notifications:', error);
    throw error;
  }
};

// Disable notifications
export const disableNotifications = async () => {
  try {
    const token = getStoredFCMToken();
    
    if (token) {
      // Remove token from server
      await removeFCMToken(token);
      
      // Remove token from localStorage
      setStoredFCMToken(null);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error disabling notifications:', error);
    throw error;
  }
};

// Setup foreground message handler
export const setupForegroundMessageHandler = async () => {
  try {
    const initialized = await initializeFirebase();
    if (!initialized) return;

    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      const { notification, data } = payload;
      if (notification) {
        // Show toast notification for in-app feedback
        toast(notification.body || 'New notification', {
          icon: '🔔',
          duration: 5000,
        });

        // Show PWA notification via Service Worker
        if (Notification.permission === 'granted' && 'serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((registration) => {
            const notificationTitle = notification.title || 'New Notification';
            const notificationOptions = {
              body: notification.body || '',
              icon: notification.icon || data?.icon || '/pwa-192x192.png',
              badge: '/pwa-192x192.png',
              tag: data?.tag || payload.messageId || 'fcm-notification',
              requireInteraction: false,
              silent: false,
              vibrate: [200, 100, 200],
              data: {
                url: data?.url || notification.click_action || '/',
                ...data
              },
              actions: data?.actions ? JSON.parse(data.actions) : undefined,
              // PWA-specific options
              renotify: true,
              timestamp: Date.now(),
            };

            registration.showNotification(notificationTitle, notificationOptions)
              .then(() => {
                console.log('PWA notification shown successfully');
              })
              .catch((error) => {
                console.error('Failed to show PWA notification:', error);
              });
          });
        }
      }
    });
  } catch (error) {
    console.error('Error setting up foreground message handler:', error);
  }
};

// Refresh FCM token (delete old and generate new)
export const refreshFCMToken = async () => {
  try {
    const initialized = await initializeFirebase();
    if (!initialized) return null;

    const oldToken = getStoredFCMToken();
    
    // Delete the old token from Firebase
    if (oldToken && messaging) {
      try {
        await deleteToken(messaging);
        console.log('Old FCM token deleted');
      } catch (error) {
        console.warn('Failed to delete old token:', error);
      }
    }
    
    // Clear from localStorage
    setStoredFCMToken(null);
    
    // Generate new token
    const newToken = await getFCMToken();
    if (newToken) {
      setStoredFCMToken(newToken);
      console.log('New FCM token generated:', newToken);
      return newToken;
    }
    
    return null;
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
    return null;
  }
};

// Validate token by checking with server
export const validateFCMToken = async (token) => {
  try {
    await storeFCMToken(token);
    return true;
  } catch (error) {
    if (error.message === 'INVALID_TOKEN') {
      return false;
    }
    // Other errors don't necessarily mean invalid token
    return true;
  }
};

// Initialize FCM on app load (if already enabled)
export const initializeFCM = async () => {
  try {
    const token = getStoredFCMToken();
    if (token && isNotificationEnabled()) {
      console.log('Checking stored FCM token validity...');
      
      // Validate token with server (silent check)
      const isValid = await validateFCMToken(token);
      if (!isValid) {
        console.warn('Stored token is invalid, refreshing automatically...');
        
        // Try to refresh the token automatically
        try {
          const newToken = await refreshFCMToken();
          if (newToken) {
            // Store new token on server
            await storeFCMToken(newToken);
            console.log('Token refreshed successfully');
            setupForegroundMessageHandler();
          } else {
            console.error('Failed to refresh token, clearing stored token');
            setStoredFCMToken(null);
          }
        } catch (error) {
          console.error('Error refreshing token:', error);
          setStoredFCMToken(null);
        }
        return;
      }
      
      setupForegroundMessageHandler();
      console.log('FCM initialized with valid stored token');
    }
  } catch (error) {
    console.error('Error initializing FCM:', error);
  }
};
